import { Print } from "@mui/icons-material";
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Snackbar, Typography } from "@mui/material";
import { LogOutIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import MessageDialog from "../../common/dialog/commonDialogBox";
import {
  fetchAdmissionPatient,
  fetchAllAdmissions,
  updateAdmission
} from "../../redux/admissions/admissionsActions";
import { addAttender, resetCurrentAttender } from "../../redux/Attender/attenderAction";
import AdmissionsTable from "./subcomponent/AdmissionsTable";
import AttenderFormDrawer from "./subcomponent/AttenderFormDrawer";
import { handlePrintAttenderPasses } from "./subcomponent/generateQRData";
import PrintAttenderDialog from "./subcomponent/PrintAttenderDialog";

const AttenderAdd = () => {
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const admissionsList = useSelector(
    (state) => state?.admission?.fetchAllAdmissions || {}
  );

  const attenderAdd = useSelector(
    (state) => state?.attender?.addAttenders || {}
  );
   const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [attendersList, setAttendersList] = useState([{ id: Date.now() }]);
  const [selectedAttenders, setSelectedAttenders] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [messageDialog, setMessageDialog] = useState({
    open: false,
    title: "",
    message: "",
    type: "info",
  });
  

  const [dischargeDialogOpen, setDischargeDialogOpen] = useState(false);
  const [selectedAdmission, setSelectedAdmission] = useState(null);
  const [dischargeType, setDischargeType] = useState("");
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
  const totalPages = admissionsList?.admissions?.totalPages || 0;

  
  const dischargeOptions = [
    { value: "3", label: "Discharged" },
    { value: "4", label: "Death" },
    { value: "5", label: "Abscond" },
    { value: "6", label: "Referral" },
  ];

  const fetchData = async (page) => {
    await dispatch(fetchAllAdmissions(page));
    setLoading(false);
  };

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage, dispatch]);
  
  useEffect(() => {
    if (attenderAdd.success) {
      setDrawerOpen(false);
      setMessageDialog({
        open: true,
        title: "Success",
        message: "Attender added successfully!",
        type: "success",
      });
      dispatch(resetCurrentAttender())
      fetchData(currentPage);
    } else if (attenderAdd.error) {
      setMessageDialog({
        open: true,
        title: "Error",
        message: attenderAdd.message || "Failed to add attender.",
        type: "error",
      });
    }
  }, [attenderAdd]);

  const handleDischargeClick = (row) => {
    if (row.statusCode === 1) {
      setSelectedAdmission(row);
      setDischargeType(""); 
      setDischargeDialogOpen(true);
    }
  };

  const handleDischargeConfirm = async () => {
    if (selectedAdmission && dischargeType) {
      const currentDate = new Date();
      const formattedDate = `${currentDate.getFullYear()}-${String(
        currentDate.getMonth() + 1
      ).padStart(2, "0")}-${String(currentDate.getDate()).padStart(
        2,
        "0"
      )} ${String(currentDate.getHours()).padStart(2, "0")}:${String(
        currentDate.getMinutes()
      ).padStart(2, "0")}`;
      
      const formdata = {
        admission_status: dischargeType,
        discharge_date: formattedDate,
      };

      try {
        await dispatch(updateAdmission(selectedAdmission.id, formdata));
        setDischargeDialogOpen(false);
        setSuccessSnackbarOpen(true);
        setDischargeType(""); 
        setSelectedAdmission(null);
        fetchData(currentPage);
      } catch (error) {
        console.error("Error discharging patient:", error);
      }
    }
  };

  const handleDischargeCancel = () => {
    setDischargeDialogOpen(false);
    setSelectedAdmission(null);
    setDischargeType(""); 
  };

  const handleDischargeTypeChange = (event) => {
    setDischargeType(event.target.value);
  };

  const handleCloseSuccessSnackbar = () => {
    setSuccessSnackbarOpen(false);
  };


  const getDischargeTypeLabel = (value) => {
    const option = dischargeOptions.find(opt => opt.value === value);
    return option ? option.label : "Updated";
  };

  const columns = [
    { id: "admissionNo", label: "Admission No", sortable: true },
    { id: "patientSerialNo", label: "Patient ID", sortable: true },
    { id: "patientName", label: "Patient Name", sortable: true },
    { id: "ipNumber", label: "IP Number", sortable: true },
    { id: "phone", label: "Phone", sortable: true },
    {
      id: "admitDate",
      label: "Admit Date",
      sortable: true,
      render: (value) => {
        const date = new Date(value);
        return isNaN(date) ? "Invalid Date" : date.toLocaleDateString();
      },
    },
    {
      id: "dischargeDate",
      label: "Discharge Date",
      sortable: true,
      render: (value) => {
        if (!value) return "Not Discharged";
        const date = new Date(value);
        return isNaN(date) ? "Invalid Date" : date.toLocaleDateString();
      },
    },
    { id: "departmentName", label: "Department", sortable: true },
    { id: "wardName", label: "Ward", sortable: true },
    { id: "numAttenders", label: "Attenders", sortable: true },
    {
      id: "status",
      label: "Status",
      sortable: true,
      render: (value) => (
        <Chip
          label={value}
          color={
            value === "Active"
              ? "success"
              : value === "In Active"
              ? "default"
              : value === "Discharged"
              ? "primary"
              : "warning"
          }
        />
      ),
    },
    {
      id: "actions",
      label: "Actions",
      render: (_, row) => (
        <Box sx={{ display: "flex", gap: 5 }}>
          <Box>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={() => handleOpenAttenderForm(row)}
            >
              Add Attender
            </Button>
          </Box>
      
          {row.statusCode === 1 && (
            <Box>
              <Button
                onClick={() => handleDischargeClick(row)}
                size="small"
                title="Discharge"
                startIcon={
                  <LogOutIcon sx={{ color: "red" }} />
                }
                sx={{ color: "red" }}
              >
                Discharge
              </Button>
            </Box>
          )}
      
          {row.attenders && row.attenders.length > 0 && (
            <Box>
              <Button
                variant="contained"
                color="info"
                size="small"
                startIcon={<Print />}
                onClick={() => handleOpenPrintDialog(row)}
                sx={{
                  bgcolor: "#2196f3",
                  color: "white",
                  "&:hover": { bgcolor: "#1976d2" },
                }}
              >
                Print Passes
              </Button>
            </Box>
          )}
        </Box>
      )
    },
  ];

  useEffect(() => {
    if (
      admissionsList.admissions?.data &&
      Array.isArray(admissionsList.admissions.data)
    ) {
      const formattedData = admissionsList.admissions.data.map((admission) => ({
        id: admission.id,
        admissionNo: admission.admission_no,
        patientId: admission.Patient?.id,
        patientSerialNo: admission.Patient?.patient_serial_no,
        patientName: admission.Patient?.patient_name,
        ipNumber: admission.Patient?.ip_number,
        phone: admission.Patient?.phone,
        admitDate: admission.admit_date,
        dischargeDate: admission.discharge_date,
        blockId: admission.Block?.id || "N/A",
        Zone: admission.Block?.zone_name || "N/A",
        floorId: admission.Floor?.id || "N/A",
        blockName: admission.Block?.block_name || "N/A",
        floorName: admission.Floor?.floor_name || "N/A",
        departmentName: admission.Department?.department_name || "N/A",
        wardName: admission.Ward?.ward_name || "N/A",
        wardId: admission.Ward?.id || "N/A",
        wardType: admission.Ward?.ward_type || "N/A",
        status: getStatusLabel(admission.admission_status),
        statusCode: parseInt(admission.admission_status),
        numAttenders: admission.number_attenders || 0,
        attenders: admission.Attenders || [],
      }));
      setData(formattedData);
    } else {
      setData([]);
    }
  }, [admissionsList]);

  const getStatusLabel = (statusCode) => {
    switch (parseInt(statusCode)) {
      case 1:
        return "Active";
      case 2:
        return "In Active";
      case 3:
        return "Discharged";
      case 4:
        return "Death";
      case 5:
        return "Abscond";
      case 6:
        return "Referral";
      default:
        return "Unknown";
    }
  };

  const handleOpenAttenderForm = (row) => {
    setSelectedPatient(row);
    setAttendersList([{ id: Date.now() }]);
    setDrawerOpen(true);
  };

  const handleOpenPrintDialog = (row) => {
    setSelectedPatient(row);
    setSelectedAttenders([]);
    setPrintDialogOpen(true);
  };

  const handleDialogClose = () => {
    setMessageDialog((prev) => ({ ...prev, open: false }));
  };

  const handleAddAttender = () => {
    setAttendersList([...attendersList, { id: Date.now() }]);
  };

  const handleRemoveAttender = (id) => {
    if (attendersList.length > 1) {
      setAttendersList(attendersList.filter((item) => item.id !== id));
    }
  };

  const onSubmitAttenders = async (formData) => {
    try {
      setSubmitting(true);
      const attenderDetails = attendersList.map((item, index) => ({
        attender_name: formData[`attender_name_${index}`],
        relation: formData[`relation_${index}`],
        phone: formData[`phone_${index}`],
        address: formData[`address_${index}`],
      }));

      const payload = {
        patient_id: selectedPatient.patientId,
        admission_id: selectedPatient.id,
        block_id: selectedPatient.blockId,
        ward_id: selectedPatient.wardId,
        floor_id: selectedPatient.floorId,
        ward_type: selectedPatient.wardType || "",
        // wardName:selectedPatient.Ward.ward_name || "",
        attender_details: attenderDetails,
      };

      const response = await dispatch(addAttender(payload));
      if (response?.payload?.status === 200 || attenderAdd?.status === 200) {
        fetchData(currentPage);
        await dispatch(fetchAdmissionPatient(selectedPatient.id));
        await handlePrintAttenderPasses({
          selectedPatient,
          attendersList,
          submitting,
          selectedAttenders: []
        });

        setTimeout(() => {
          setDrawerOpen(false);
          setSubmitting(false);
        }, 500);
      } else {
        console.error(
          "Error adding attenders:",
          response?.payload?.message || "Unknown error"
        );
        setSubmitting(false);
      }
    } catch (error) {
      console.error("Error adding attenders:", error);
      setSubmitting(false);
    }
    fetchData(currentPage);
  };
  
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrintSelected = async () => {
    if (selectedAttenders.length === 0) {
      return;
    }

    await handlePrintAttenderPasses({
      selectedPatient,
      attendersList: [],
      submitting: false,
      selectedAttenders
    });
    
    setPrintDialogOpen(false);
  };

  return (
    <Box>
      <AdmissionsTable 
        columns={columns}
        data={data}
        isLoading={loading}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />

      <AttenderFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        selectedPatient={selectedPatient}
        attendersList={attendersList}
        handleAddAttender={handleAddAttender}
        handleRemoveAttender={handleRemoveAttender}
        onSubmit={onSubmitAttenders}
        submitting={submitting}
      />

      <PrintAttenderDialog
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
        selectedPatient={selectedPatient}
        selectedAttenders={selectedAttenders}
        setSelectedAttenders={setSelectedAttenders}
        handlePrintSelected={handlePrintSelected}
      />

      <MessageDialog
        open={messageDialog.open}
        onClose={handleDialogClose}
        title={messageDialog.title}
        message={messageDialog.message}
        type={messageDialog.type}
      />

      
      <Dialog
        open={dischargeDialogOpen}
        onClose={handleDischargeCancel}
        aria-labelledby="discharge-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          id="discharge-dialog-title"
          sx={{ bgcolor: "red", color: "white" }}
        >
          Discharge Patient
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography sx={{ mb: 3 }}>
            Please select discharge type for patient:
            {selectedAdmission && (
              <Box component="span" fontWeight="bold">
                {" "}
                {selectedAdmission.patientName}
              </Box>
            )}
          </Typography>
          
          <FormControl fullWidth variant="outlined">
            <InputLabel id="discharge-type-label">Discharge Type</InputLabel>
            <Select
              labelId="discharge-type-label"
              id="discharge-type-select"
              value={dischargeType}
              onChange={handleDischargeTypeChange}
              label="Discharge Type"
            >
              {dischargeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleDischargeCancel}
            sx={{
              backgroundColor: "orange",
              color: "white",
              "&:hover": {
                backgroundColor: "darkorange",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDischargeConfirm}
            variant="contained"
            disabled={!dischargeType} 
            sx={{
              backgroundColor: dischargeType ? "green" : "grey",
              color: "white",
              "&:hover": {
                backgroundColor: dischargeType ? "darkgreen" : "grey",
              },
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={successSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSuccessSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSuccessSnackbar}
          severity="success"
          sx={{ width: "100%", bgcolor: "green", color: "white" }}
        >
          Patient {getDischargeTypeLabel(dischargeType)} successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AttenderAdd;